import React, { useEffect, useRef, useState } from "react";
import { v4_chat_message_fetch, v4_chat_message_send } from "../../actions/v4/mailBox";
import { getSessionStorage } from "../../utils";
import moment from "moment";
import constant from "../../constant";
import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";
import { toast } from "react-toastify";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { useMessageContext } from "../../context/MessageContext"; // 👈 Context hook
import { formatLocalDate, formatLocalTime, getLocalDayKey, toValidDate } from "../../dateTimeFormat";
import { ChatAttachmentPreviewList, buildChatAttachments, getChatAttachmentDownloadUrl } from "./chatAttachmentPreview";

const ChatWindow = ({ onlineUsers, user }) => {
    const CHAT_HISTORY_LIMIT = 100;
    const getFileExtensionLabel = (file = {}) => {
        const fileName = String(file?.name || '');
        const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
        return extension ? extension.toUpperCase() : 'FILE';
    };
    const [messages, setMessages] = useState([]);
    const [ud, set_ud] = useState({});
    const { newMessages } = useMessageContext(); // 👈 Access new messages
    const [state, setState] = useState({
        message: { name: "message", value: "", error: "", isRequired: false },
        file: { name: "file", value: [], error: "", isRequired: false },
        tagged_message: { name: "tagged_message", value: "", error: "", isRequired: false },
    });
    const [isSendingMessage, setIsSendingMessage] = useState(false);

    const isCurrentUserOnline = onlineUsers?.includes(Number(user.USER_ID));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState((prevState) => ({
            ...prevState,
            [name]: {
                ...prevState[name],
                value,
                error: "",
            },
        }));
    };

    // 👇 Fetch initial chat history
    useEffect(() => {
        if (!user.USER_ID) return;

        v4_chat_message_fetch(user.USER_ID, CHAT_HISTORY_LIMIT).then((res) => {
            if (res.status) {
                setMessages(res.result);
            } else {
                setMessages([]);
            }
        });
    }, [user.USER_ID]);

    // 👇 Append real-time incoming messages
    useEffect(() => {
        if (!user.USER_ID || !Array.isArray(newMessages) || newMessages.length === 0) {
            return;
        }

        const targetUserId = Number(user.USER_ID);

        setMessages((prev) => {
            const existingIds = new Set(prev.map((message) => message?.id));
            const incoming = newMessages
                .filter((msg) => Number(msg?.from) === targetUserId || Number(msg?.to) === targetUserId)
                .map((msg) => msg?.messageData)
                .filter((msg) => msg?.id && !existingIds.has(msg.id));

            if (incoming.length === 0) {
                return prev;
            }

            return [...prev, ...incoming].slice(-CHAT_HISTORY_LIMIT);
        });
    }, [newMessages, user.USER_ID]);
    const onSendMessage = async (e) => {
        if (e) {
            e.preventDefault();
        }

        // if (validateFunctionalForm(state, setState))
        if (isSendingMessage || (!state.message.value && state.file.value.length === 0)) {
            return;
        }

        const outgoingText = state.message.value;
        const outgoingTaggedMessage = state.tagged_message.value;
        const outgoingFiles = [...state.file.value];

        try {
            setIsSendingMessage(true);
            const formData = new FormData();
            formData.append("message", outgoingText);
            formData.append("tagged_message", outgoingTaggedMessage || "");
            outgoingFiles.forEach((file) => formData.append("files", file));

            const res = await v4_chat_message_send(user.USER_ID, formData);
            if (res.status) {
                const newMessage = {
                    id: res.result.id,
                    text: outgoingText,
                    tagged_message: outgoingTaggedMessage,
                    send_on: moment().format("YYYY-MM-DDTHH:mm:ss"),
                    from_user: ud.fullName,
                    to_user: user.FULL_NAME,
                    type: "sent",
                    file_name: outgoingFiles.map((item) => item.name),
                    file_url: res.result.file_url,
                };

                setMessages((prev) => [...prev, newMessage].slice(-CHAT_HISTORY_LIMIT));

                setState((prev) => ({
                    ...prev,
                    message: { ...prev.message, value: "" },
                    tagged_message: { ...prev.tagged_message, value: "" },
                    file: { ...prev.file, value: [] },
                }));
            } else {
                toast.error(res.error || "Failed to send message");
            }
        } catch (error) {
            toast.error("Failed to send message");
        } finally {
            setIsSendingMessage(false);
        }
    };

    useEffect(() => {
        if (getSessionStorage(constant.keys.userDetails)) {
            set_ud(JSON.parse(atob(getSessionStorage(constant.keys.userDetails))));
        }
    }, []);

    const imageInputRef = useRef(null);
    const fileInputRef = useRef(null);

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const inputRef = useRef(null);

    const handleEmojiClick = () => {
        setShowEmojiPicker(!showEmojiPicker);
    };

    const addEmoji = (emoji) => {
        const message = (state.message.value || "") + emoji.native;
        handleChange({ target: { name: state.message.name, value: message } });
    };

    const handleImageClick = () => {
        imageInputRef.current.click();
    };

    const handleFileClick = () => {
        fileInputRef.current.click();
    };

    const onImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setState((prevState) => ({
                ...prevState,
                file: { ...prevState.file, value: files, error: "" },
            }));
        }
    };

    const onFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setState((prevState) => ({
                ...prevState,
                file: { ...prevState.file, value: files, error: "" },
            }));
        }
    };

    const conversationBodyRef = useRef(null);
    useEffect(() => {
        if (conversationBodyRef.current) {
            window.requestAnimationFrame(() => {
                if (!conversationBodyRef.current) return;
                conversationBodyRef.current.scrollTop = conversationBodyRef.current.scrollHeight;
            });
        }
    }, [messages]);

    const handleRemoveFile = (index) => {
        setState((prevState) => ({
            ...prevState,
            file: {
                ...prevState.file,
                value: prevState.file.value.filter((_, i) => i !== index),
            },
        }));
    };

    const [filePreviewUrls, setFilePreviewUrls] = useState([]);
    useEffect(() => {
        const files = Array.isArray(state.file.value) ? state.file.value : [];
        const nextUrls = files.map((file) => URL.createObjectURL(file));
        setFilePreviewUrls(nextUrls);

        return () => {
            nextUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [state.file.value]);

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard
            .writeText(text)
            .then(() => toast.success("Message copied to clipboard!"))
            .catch(() => alert("Failed to copy!"));
    };

    const todayDayKey = getLocalDayKey(new Date());
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayDayKey = getLocalDayKey(yesterday);

    return (
        <div className="email-rightbar mb-3">
            <div className="card" style={{ height: '678px', display: 'flex', flexDirection: 'column' }}>
                <div className="p-4 border-bottom ">
                    <div className="row">
                        <div className="col-md-4 col-9">
                            <h5 className="font-size-15 mb-1">{user?.FULL_NAME}</h5>
                            <p className="text-muted mb-0">
                                <i className={`mdi mdi-circle ${isCurrentUserOnline ? 'text-success' : 'text-danger'} align-middle me-1`} />{" "}
                                {isCurrentUserOnline ? 'Active now' : 'Offline'}
                            </p>
                        </div>
                        <div className="col-md-8 col-3">

                        </div>
                    </div>
                </div>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <div className="chat-conversation p-3" style={{ flex: 1, minHeight: 0, paddingBottom: 0 }}>
                        <ul
                            ref={conversationBodyRef}
                            className="list-unstyled mb-0"
                            tabIndex={0}
                            role="region"
                            aria-label="Chat messages"
                            style={{ height: '100%', overflowY: 'auto', paddingRight: '8px', paddingBottom: '16px' }}
                        >
                            {messages.length === 0 ? (
                                <li className="text-center text-muted py-5">
                                    <i className="fa fa-comments fa-2x mb-2" />
                                    <p className="mb-0">No messages yet</p>
                                </li>
                            ) : (
                                messages.map((message, index) => {
                                    const currentDate = toValidDate(message.send_on);
                                    const currentDayKey = getLocalDayKey(currentDate);
                                    const previousDayKey = index > 0
                                        ? getLocalDayKey(messages[index - 1]?.send_on)
                                        : '';
                                    const isFirstOfDay = Boolean(currentDayKey) && currentDayKey !== previousDayKey;
                                    const dateLabel = currentDayKey === todayDayKey
                                        ? 'Today'
                                        : currentDayKey === yesterdayDayKey
                                            ? 'Yesterday'
                                            : formatLocalDate(currentDate);
                                    const messageTime = formatLocalTime(currentDate);
                                    const attachments = buildChatAttachments(message);
                                    const primaryAttachmentUrl = attachments[0]?.url || '';

                                    return (
                                        <React.Fragment key={index}>
                                            {isFirstOfDay && (
                                                <li>
                                                    <div className="chat-day-title">
                                                        <span className="title">{dateLabel}</span>
                                                    </div>
                                                </li>
                                            )}

                                            {message.type === 'sent' && (
                                                <li>
                                                    <div className="conversation-list">
                                                        <div className="dropdown">
                                                            <a
                                                                className="dropdown-toggle"
                                                                href="#"
                                                                role="button"
                                                                data-bs-toggle="dropdown"
                                                                aria-haspopup="true"
                                                                aria-expanded="false"
                                                            >
                                                                <i className="bx bx-dots-vertical-rounded" />
                                                            </a>
                                                            <div className="dropdown-menu">
                                                                <a className="dropdown-item" href="javascript:void(0);" onClick={() => { copyToClipboard(message.text); }}>Copy</a>
                                                                {primaryAttachmentUrl && (
                                                                    <a className="dropdown-item" href={getChatAttachmentDownloadUrl(primaryAttachmentUrl)} target="_blank" rel="noopener noreferrer">
                                                                        Download
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="ctext-wrap">
                                                            <div className="conversation-name">{message.from_user}</div>
                                                            <ChatAttachmentPreviewList message={message} />
                                                            {message?.text ? <p>{message.text}</p> : null}
                                                            <p className="chat-time mb-0">
                                                                <i className="bx bx-time-five align-middle me-1" />
                                                                {messageTime}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </li>
                                            )}

                                            {message.type === 'received' && (
                                                <li className="right">
                                                    <div className="conversation-list">
                                                        <div className="dropdown">
                                                            <a
                                                                className="dropdown-toggle"
                                                                href="#"
                                                                role="button"
                                                                data-bs-toggle="dropdown"
                                                                aria-haspopup="true"
                                                                aria-expanded="false"
                                                            >
                                                                <i className="bx bx-dots-vertical-rounded" />
                                                            </a>
                                                            <div className="dropdown-menu">
                                                                <a className="dropdown-item" href="javascript:void(0);" onClick={() => { copyToClipboard(message.text); }}>Copy</a>
                                                                {primaryAttachmentUrl && (
                                                                    <a className="dropdown-item" href={getChatAttachmentDownloadUrl(primaryAttachmentUrl)} target="_blank" rel="noopener noreferrer">
                                                                        Download
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="ctext-wrap">
                                                            <div className="conversation-name">{message.from_user}</div>
                                                            <ChatAttachmentPreviewList message={message} />
                                                            {message?.text ? <p>{message.text}</p> : null}
                                                            <p className="chat-time mb-0">
                                                                <i className="bx bx-time-five align-middle me-1" />
                                                                {messageTime}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </li>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                            {state.file.value?.length > 0 && (
                                <li>
                                    <div className="file-preview-container mb-2 d-flex flex-wrap gap-2">
                                        {state.file.value.map((file, idx) => {
                                            const isImage = file.type.startsWith("image/");
                                            const fileURL = filePreviewUrls[idx];

                                            return (
                                                <div key={idx} className={`preview-item position-relative border rounded p-1 chat-file-preview-item ${isSendingMessage ? "is-uploading" : ""}`}>
                                                    {isImage && fileURL ? (
                                                        <img src={fileURL} alt="preview" width={80} height={80} className="rounded chat-file-preview-image" />
                                                    ) : (
                                                        <div className="chat-file-preview-document">
                                                            <div className="chat-file-preview-icon">
                                                                <i className="bx bx-file" aria-hidden="true"></i>
                                                            </div>
                                                            <div className="chat-file-preview-details">
                                                                <span className="chat-file-preview-name" title={file.name}>{file.name}</span>
                                                                <span className="chat-file-preview-type">{getFileExtensionLabel(file)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {isSendingMessage && (
                                                        <div className="chat-uploading-overlay">
                                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                            <span>Uploading...</span>
                                                        </div>
                                                    )}
                                                    {!isSendingMessage && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveFile(idx)}
                                                            className="chat-file-preview-remove"
                                                            aria-label={`Remove ${file.name}`}
                                                        >
                                                            &times;
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </li>
                            )}
                        </ul>
                    </div>
                    <div className="p-2 chat-input-section">
                        <form className="row" onSubmit={(e) => onSendMessage(e)}>
                            <div className="col">
                                <div className="position-relative">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        style={{ height: '50px' }}
                                        className="form-control chat-input"
                                        placeholder="Enter Message..."
                                        name={state.message.name}
                                        value={state.message.value}
                                        onChange={handleChange}
                                        disabled={isSendingMessage}
                                    />

                                    {showEmojiPicker && (
                                        <div style={{ position: "absolute", bottom: "50px", left: "0", zIndex: 1000 }}>
                                            <Picker onSelect={addEmoji} />
                                        </div>
                                    )}
                                    <div className="chat-input-links" id="tooltip-container">
                                        <ul className="list-inline mb-0">
                                            <li className="list-inline-item">
                                                <button
                                                    type="button"
                                                    onClick={handleEmojiClick}
                                                    title="Emoji"
                                                    className="btn btn-link p-0"
                                                    disabled={isSendingMessage}
                                                >
                                                    <i className="mdi mdi-emoticon-happy-outline fs-2" />
                                                </button>
                                            </li>
                                            <li className="list-inline-item">
                                                <button
                                                    type="button"
                                                    onClick={handleImageClick}
                                                    title="Images"
                                                    className="btn btn-link p-0"
                                                    disabled={isSendingMessage}
                                                >
                                                    <i className="mdi mdi-file-image-outline fs-2" />
                                                </button>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    style={{ display: "none" }}
                                                    ref={imageInputRef}
                                                    onChange={onImageChange}
                                                />
                                            </li>
                                            <li className="list-inline-item">
                                                <button
                                                    type="button"
                                                    onClick={handleFileClick}
                                                    title="Add Files"
                                                    className="btn btn-link p-0"
                                                    disabled={isSendingMessage}
                                                >
                                                    <i className="mdi mdi-file-document-outline fs-2" />
                                                </button>
                                                <input
                                                    type="file"
                                                    multiple
                                                    style={{ display: "none" }}
                                                    ref={fileInputRef}
                                                    onChange={onFileChange}
                                                />
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="col-auto">
                                <button
                                    style={{ backgroundColor: 'rgb(46 91 127)', marginTop: '5px' }}
                                    type="submit"
                                    className="btn btn-primary btn-rounded chat-send w-md waves-effect waves-light"
                                    disabled={isSendingMessage}
                                >
                                    <span className="d-none d-sm-inline-block me-2">{isSendingMessage ? "Sending" : "Send"}</span>{" "}
                                    {isSendingMessage ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    ) : (
                                        <i className="mdi mdi-send" />
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default withRouter(ChatWindow);
